import React, { useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    Paper,
    Skeleton,
    Stack,
    TextField,
} from "@mui/material";
import Icon from "src/components/icon";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import request from "src/utils/request";
import { ToastContext } from "src/components/providers/toast-provider";
import { useNavigate } from "react-router-dom";
import fetchStatus from "src/utils/api/fetch-status";
import ReportIcon from "@mui/icons-material/Report";
import { useActiveIntegrations } from "src/scenes/dashboard/active-integrations-provider";
import { useAuth } from "src/components/providers/auth-context";
import { format } from "date-fns";
import SaveIcon from "@mui/icons-material/Save";
import googleWorkspace from "../../../assets/icons/google-workspace";

const H1 = styled.h1`
    font-size: 22px;
    margin: 0;
    font-weight: normal;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const H2 = styled.h2`
    font-size: 14px;
    font-weight: normal;
    margin: 0;
    color: ${({ theme }) => theme.palette.primary.main};
`;

const P = styled.h2`
    font-size: 12px;
    font-weight: normal;
    margin: 18px;
    color: ${({ theme }) => theme.palette.secondary.main};
`;

const P2 = styled.h2`
    font-size: 12px;
    font-weight: normal;
    margin: 0;
    color: ${({ theme }) => theme.palette.secondary.main};
`;

const PLabel = styled.h2`
    font-size: 12px;
    font-weight: normal;
    margin: 18px 18px 18px 0;
    color: ${({ theme }) => theme.palette.secondary.main};
`;

const CustomPaper = styled(Paper)`
    border-radius: 14px;
    width: 100%;
    cursor: pointer;
    transition: opacity 0.4s ease-out;
    position: static;
`;

const PermissionsIcon = styled(Icon)`
    background-color: #fff;
    border-radius: 16px;
    padding: 24px 12px 24px 12px;
    margin: 32px 0 32px 0;
`;

const DisconnectButton = styled(Button)`
    width: 136px;
    height: 36px;
`;

const PaperTitle = styled.p`
    margin: 0;
    padding: 7px 0 6px 18px;
    font-size: 11px;
    background-color: #5b5b5b;
    border-radius: 14px 14px 0 0;
    backface-visibility: hidden;
    transform: translateZ(0) scale(1, 1);
`;

const SettingsGoogleOrg = () => {
    const [settings, setSettings] = React.useState(undefined);
    const [disconnectSelected, isDisconnectSelected] = React.useState(false);
    const [newUserId, setNewUserId] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const [settingsStatus, setSettingsStatus] = useState();
    const { setMessage, setSeverity, setIsToastTriggered } = React.useContext(ToastContext);
    const [centrallyManagedIntegrations, setCentrallyManagedIntegrations] = useState();

    const { user } = useAuth();
    const { activeIntegrations: userIntegrations } = useActiveIntegrations();
    const navigate = useNavigate();

    const getSettings = async () => {
        try {
            const response = await request(`/api/google`, { method: "GET" });
            setSettings(response);
            setLoading(false);
        } catch (e) {
            setSettingsStatus(e.status);
            setSettings(undefined);
            setLoading(false);
        }
    };

    React.useEffect(() => {
        getSettings();
    }, []);

    React.useEffect(() => {
        if (userIntegrations.status === fetchStatus.SUCCESS) {
            const googleIntegration = userIntegrations.data.integrations.find(
                (item) => item.name === "google"
            );
            if (googleIntegration) {
                setCentrallyManagedIntegrations(!!googleIntegration.centrallyManaged);
            }
        }
    }, [userIntegrations.status]);

    const handleDisconnectChange = (event) => {
        isDisconnectSelected(event.target.checked);
    };

    const handleDisconnect = async () => {
        try {
            await request("/api/google/integration", { method: "DELETE" });
            userIntegrations.refetch();
            setMessage("Successfully disconnected Google integration. We are sad to see you go..");
            setSeverity("success");
            setIsToastTriggered(true);
            navigate("/integrations");
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            setMessage("Failed to disconnect Google. Please try again.");
            setSeverity("warning");
            setIsToastTriggered(true);
        }
        return undefined;
    };

    const assignAccounts = async () => {
        await request(`/api/users/me/dormant-integrations`, {
            method: "PUT",
            body: JSON.stringify([
                {
                    serviceId: `${user.orgId}:${user.id}:${newUserId}`,
                    integration: "google",
                },
            ]),
        });
        setMessage(`Google email changed successfully`);
        setSeverity("success");
        setIsToastTriggered(true);
    };

    const handleSubmitUserId = async () => {
        try {
            assignAccounts();
        } catch {
            setMessage(`Something went wrong.`);
            setSeverity("warning");
            setIsToastTriggered(true);
        } finally {
            setNewUserId(undefined);
            setLoading(true);
            getSettings();
        }
    };

    const renderSettingsLine = (fieldName) => {
        if (settings) {
            if (fieldName === "createdAt")
                return (
                    <P style={{ marginLeft: 0 }}>
                        {format(new Date(settings.createdAt * 1000), "EEE d MMM yyyy")}
                    </P>
                );
            return <P style={{ marginLeft: 0 }}>{settings[fieldName] || "OK"}</P>;
        }
        if (centrallyManagedIntegrations && settingsStatus === 404) {
            if (fieldName === "integrationStatus")
                return (
                    <P style={{ marginLeft: 0 }}>Google account was not automatically detected</P>
                );
            return <P style={{ marginLeft: 0 }}>N/A</P>;
        }
        return <Skeleton variant="rectangular" width={210} height={22} />;
    };

    return (
        <>
            <Helmet>
                <title>Your Google settings</title>
            </Helmet>
            <Grid container justifyContent="center" mb={4}>
                <Stack
                    direction="column"
                    alignItems="flex-start"
                    style={{ height: "100%", width: "70%" }}
                >
                    <Stack direction="row" alignItems="center">
                        <PermissionsIcon svg={googleWorkspace} width="100px" height="72px" />
                        <Stack direction="column" ml={2}>
                            <H1>
                                Google Settings
                                {centrallyManagedIntegrations && <H1>(Centrally Managed)</H1>}
                            </H1>
                            <H2>settings for your Google integration</H2>
                        </Stack>
                    </Stack>
                    {settings && settings.invalid === true && (
                        <CustomPaper style={{ marginBottom: "14px", padding: "7px" }}>
                            <Stack direction="row" alignItems="center">
                                <Box
                                    style={{
                                        backgroundColor: "darkorange",
                                        borderRadius: "46px",
                                        margin: "18px",
                                        height: "46px",
                                        width: "46px",
                                    }}
                                >
                                    <ReportIcon style={{ margin: "11px", color: "#303030" }} />
                                </Box>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    style={{ width: "88%" }}
                                >
                                    <Stack direction="column">
                                        <P2 style={{ fontSize: "14px", marginBottom: "8px" }}>
                                            <strong>{settings.invalidationError}</strong>
                                        </P2>
                                        <P2>{settings.invalidationReason}</P2>
                                        <P2>
                                            You might be viewing partial or incorrect data.
                                            Reconnect your Google account.
                                        </P2>
                                    </Stack>
                                    {/* <Button onClick={handleReconnect}>Reconnect Google</Button> */}
                                </Stack>
                            </Stack>
                        </CustomPaper>
                    )}
                    <CustomPaper>
                        <PaperTitle>Integration Information</PaperTitle>
                        <>
                            <Stack direction="row" alignItems="center">
                                <P>
                                    <strong>Account created:</strong>{" "}
                                </P>
                                {renderSettingsLine("createdAt")}
                            </Stack>
                            <Divider />
                            <Stack direction="row" alignItems="center">
                                <P>
                                    <strong>Google email:</strong>
                                </P>
                                {renderSettingsLine("email")}
                            </Stack>
                            <Divider />
                            <Stack direction="row" alignItems="center">
                                <P>
                                    <strong>Integration status:</strong>
                                </P>
                                {renderSettingsLine("integrationStatus")}
                            </Stack>
                        </>
                    </CustomPaper>
                    {centrallyManagedIntegrations && settings && !loading && (
                        <Stack direction="column">
                            <P style={{ fontSize: "16px", marginBottom: 0 }}>Change email</P>

                            <Stack direction="row" alignItems="flex-start">
                                <TextField
                                    error={newUserId === settings && settings.email}
                                    id="outlined-basic"
                                    label="Googl email"
                                    variant="outlined"
                                    size="small"
                                    value={newUserId}
                                    helperText={
                                        newUserId === settings &&
                                        settings.email &&
                                        "This Google email is already in use"
                                    }
                                    style={{ margin: "16px", minWidth: "284px" }}
                                    onChange={(event) => {
                                        setNewUserId(event.target.value);
                                    }}
                                    InputProps={{
                                        sx: {
                                            fontSize: "12px",
                                            fontWeight: 300,
                                            padding: "3.5px",
                                        },
                                    }}
                                    InputLabelProps={{
                                        sx: {
                                            fontSize: "12px",
                                            fontWeight: 300,
                                            padding: "3.5px",
                                            "&.MuiOutlinedInput-notchedOutline": {
                                                fontSize: "13px",
                                            },
                                        },
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    style={{ height: "41px", margin: "16px" }}
                                    disabled={
                                        (settings && settings.email === newUserId) || !newUserId
                                    }
                                    startIcon={<SaveIcon />}
                                    onClick={handleSubmitUserId}
                                >
                                    Save
                                </Button>
                            </Stack>
                        </Stack>
                    )}
                    {centrallyManagedIntegrations && !settings && !loading && (
                        <Stack direction="column">
                            <P style={{ fontSize: "16px", marginBottom: 0 }}>Set email</P>

                            <Stack direction="row" alignItems="flex-start">
                                <TextField
                                    id="outlined-basic"
                                    label="Google email"
                                    variant="outlined"
                                    size="small"
                                    value={newUserId}
                                    style={{ margin: "16px", minWidth: "284px" }}
                                    onChange={(event) => {
                                        setNewUserId(event.target.value);
                                    }}
                                    InputProps={{
                                        sx: {
                                            fontSize: "12px",
                                            fontWeight: 300,
                                            padding: "3.5px",
                                        },
                                    }}
                                    InputLabelProps={{
                                        sx: {
                                            fontSize: "12px",
                                            fontWeight: 300,
                                            padding: "3.5px",
                                            "&.MuiOutlinedInput-notchedOutline": {
                                                fontSize: "13px",
                                            },
                                        },
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    style={{ height: "41px", margin: "16px" }}
                                    startIcon={<SaveIcon />}
                                    onClick={handleSubmitUserId}
                                >
                                    Save
                                </Button>
                            </Stack>
                        </Stack>
                    )}
                    <P style={{ fontSize: "16px", marginBottom: 0 }}>Disconnect Google</P>
                    <Stack
                        direction="row"
                        alignItems="center"
                        style={{ width: "100%", padding: "0px 16px" }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={disconnectSelected}
                                    onChange={handleDisconnectChange}
                                />
                            }
                            label={
                                <PLabel>
                                    Disconnect integration Adadot integration with Google and remove
                                    data.
                                </PLabel>
                            }
                        />
                        {disconnectSelected && (
                            <DisconnectButton variant="contained" onClick={handleDisconnect}>
                                Disconnect
                            </DisconnectButton>
                        )}
                    </Stack>
                </Stack>
            </Grid>
        </>
    );
};

export default SettingsGoogleOrg;
